import clsx from "clsx";
import { FC, Suspense, useState } from "react";
import { useFragment, useLazyLoadQuery, useSubscription } from "react-relay";
import { graphql } from "relay-runtime";
import { Asset } from "rujira.js";
import {
  Button,
  Decimal,
  Fiat,
  i18n,
  IconDenom,
  Icons,
  nFormatter,
  useGlobalModalContext,
} from "rujira.ui";
import { BalanceProvider } from "../../common/components/Balance";
import { PositionBorrowFragment$key } from "./__generated__/PositionBorrowFragment.graphql";
import { PositionBorrowQuery } from "./__generated__/PositionBorrowQuery.graphql";
import {
  PositionBorrowRowFragment$data,
  PositionBorrowRowFragment$key,
} from "./__generated__/PositionBorrowRowFragment.graphql";
import { PositionPositionFragment$key } from "./__generated__/PositionPositionFragment.graphql";
import { LiquidationPrice, LtvLabel } from "./Health";
import { Increase } from "./PositionIncrease";
import { Repay } from "./PositionRepay";
import { Swap } from "./PositionSwap";
import { CdpSingle } from "./utils";

const query = graphql`
  query PositionBorrowQuery {
    ghostCredit {
      address
      adjustmentThreshold
    }
  }
`;

const position = graphql`
  fragment PositionBorrowFragment on GhostCreditAccount {
    id
    account {
      address
      label
    }
    ...PositionPositionFragment
  }
`;

const positionFragment = graphql`
  fragment PositionPositionFragment on GhostCreditAccount {
    collaterals {
      collateral {
        __typename
        ... on Balance {
          asset {
            asset
            type
            chain
            metadata {
              symbol
            }
            variants {
              native {
                denom
              }
            }
          }
          amount
        }
      }
      valueFull
      valueAdjusted
    }
    debts {
      value
      debt {
        current
        borrower {
          vault {
            status {
              debtRate
            }
          }
          asset {
            asset
            type
            chain
            metadata {
              symbol
            }
            variants {
              native {
                denom
              }
            }
          }
        }
      }
    }
    ltv
  }
`;

const subscription = graphql`
  subscription PositionSubscription($contract: Address!) {
    ghostCreditAccountUpdated(address: $contract) {
      node {
        ... on GhostCreditAccount {
          ...PositionPositionFragment
        }
      }
    }
  }
`;

enum Tab {
  Repay,
  Increase,
  Swap,
}

export const Position: FC<{
  hideModal: () => void;
  k: PositionBorrowFragment$key;
}> = ({ hideModal, k }) => {
  const { ghostCredit } = useLazyLoadQuery<PositionBorrowQuery>(query, {});
  const data_ = useFragment(position, k);
  const data = useFragment<PositionPositionFragment$key>(
    positionFragment,
    data_
  );
  const [tab, setTab] = useState<Tab>(Tab.Repay);
  const p = CdpSingle.fromGraph(
    { ...data_, ...data },
    ghostCredit || { adjustmentThreshold: 0n }
  ) as CdpSingle<Asset>;
  useSubscription({
    subscription,
    variables: { contract: data_.account.address },
  });

  return (
    <>
      <i18n.h2 className="h4 mb-2">Manage Position</i18n.h2>
      <div
        className="card p-2"
        style={{ marginLeft: "-0.5rem", marginRight: "-0.5rem" }}>
        <div className="row wrap pad gap-y-3">
          <div className="col-12 col-md-6">
            <h3 className="fs-14 fw-500 color-grey mb-1">Deposited</h3>
            <div className="flex ai-c">
              <IconDenom
                denom={p.collateral.asset.metadata.symbol}
                className="w-3 mr-1"
              />
              <div className="flex ai-b">
                <Decimal
                  className="fs-18 fw-500 condensed"
                  amount={p.collateral.amount}
                  symbol={p.collateral.asset.metadata.symbol}
                />
                <div className="tag tag--borderless ml-1">
                  ${nFormatter(p.collateral.value, 2, 8)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <h3 className="fs-14 fw-500 color-grey mb-1">Borrowed</h3>
            <div className="flex ai-c">
              <IconDenom
                denom={p.debt.asset.metadata.symbol || ""}
                className="w-3 mr-1"
              />
              <div className="flex ai-b">
                <Decimal
                  className="fs-18 fw-500 condensed"
                  amount={p.debt.amount}
                  symbol={p.debt.asset.metadata.symbol}
                />
                <div className="tag tag--borderless ml-1">
                  ${nFormatter(p.debt.value, 2, 8)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <h3 className="fs-14 fw-500 color-grey mb-1">Interest Rate</h3>
            <div className="tag tag--borderless tag--grey i-fs-16">
              {(Number(p.debt.rate) / 1e10).toLocaleDecimal(2)}%
            </div>
            {/* <a
            href="https://docs.rujira.network/core-products/ruji-lending"
            target="_blank"
            rel="noreferrer"
            className="mt-1 fs-12 lh-12 block no-underline color-grey hover-white">
            Variable interest rates
            <External className="h-lh w-a ml-0.5 inline-block" />
          </a> */}
          </div>
          <div className="col-6 col-md-3">
            <h3 className="fs-14 fw-500 color-grey mb-1">LTV</h3>
            <LtvLabel ltv={BigInt(data.ltv)} />
          </div>
          <div className="col-12 col-md-3">
            <h3 className="fs-14 fw-500 color-grey mb-1">
              {p.collateral.asset.metadata.symbol} Price
            </h3>
            <Fiat
              className="fs-16"
              amount={p.collateral.price}
              decimals={12}
              symbol="$"
            />
          </div>
          <div className="col-12 col-md-3">
            <h3 className="fs-14 fw-500 color-grey mb-1">Liquidation Price</h3>
            <div className="flex ai-b">
              <LiquidationPrice p={p} />
            </div>
          </div>
        </div>
      </div>
      {/* <hr className="hr opacity-10 my-3" /> */}

      <nav className="tabs tabs--sm tabs--dark mt-3 flex jc-c wrap">
        <a
          onClick={() => setTab(Tab.Repay)}
          className={clsx({ selected: tab === Tab.Repay })}>
          Repay Loan
        </a>
        <a
          onClick={() => setTab(Tab.Increase)}
          className={clsx({ selected: tab === Tab.Increase })}>
          Increase Loan
        </a>
        <a
          onClick={() => setTab(Tab.Swap)}
          className={clsx({ selected: tab === Tab.Swap })}>
          Swap Collateral
        </a>

        {/* <a
          onClick={() => setTab("advanced")}
          className={clsx({ selected: tab === "advanced" })}>
          Advanced
        </a> */}
      </nav>

      {tab === Tab.Repay && (
        <BalanceProvider asset={p.debt.asset}>
          <Repay
            hideModal={hideModal}
            p={p}
            registry={ghostCredit?.address || ""}
          />
        </BalanceProvider>
      )}
      {tab === Tab.Increase && (
        <BalanceProvider asset={p.collateral.asset}>
          <Increase
            hideModal={hideModal}
            p={p}
            registry={ghostCredit?.address || ""}
          />
        </BalanceProvider>
      )}
      {tab === Tab.Swap && (
        <Suspense>
          <Swap
            hideModal={hideModal}
            p={p}
            registry={ghostCredit?.address || ""}
          />
        </Suspense>
      )}
    </>
  );
};

const rowFragment = graphql`
  fragment PositionBorrowRowFragment on GhostCreditAccount {
    account {
      address
      label
    }
    collaterals {
      collateral {
        __typename
        ... on Balance {
          asset {
            asset
            metadata {
              symbol
            }
          }
          amount
        }
      }
      valueFull
      valueAdjusted
    }
    debts {
      value
      debt {
        current
        borrower {
          vault {
            status {
              debtRate
            }
          }
          asset {
            metadata {
              symbol
            }
          }
        }
      }
    }
    ltv
    ...PositionBorrowFragment
  }
`;

export const PositionRow: FC<{
  k: PositionBorrowRowFragment$key;
  adjustmentThreshold: bigint;
}> = ({ k, adjustmentThreshold }) => {
  const data = useFragment(rowFragment, k);
  const { showModal, hideModal } = useGlobalModalContext();
  useSubscription({
    subscription,
    variables: { contract: data.account.address },
  });

  const openModal = () => {
    showModal({
      title: ``,
      className: "modal--xl",
      backgroundClose: false,
      children: <Position k={data} hideModal={hideModal} />,
    });
  };
  const p = CdpSingle.fromGraph(data, {
    adjustmentThreshold,
  }) as CdpSingle<Asset>;
  return (
    <tr className="pointer" onClick={() => openModal()}>
      <td>
        {data.collaterals.map((x, idx) => (
          <PostitionRowCollateral key={idx} {...x} />
        ))}
        <Fiat
          amount={data.collaterals.reduce((a, v) => a + v.valueFull, 0n)}
          symbol="$"
          className="color-grey fs-14 condensed ml-8"
          decimals={8}
        />
      </td>
      <td>
        {data.debts.map((x, idx) => (
          <PostitionRowDebt key={idx} {...x} />
        ))}
        <Fiat
          amount={data.debts.reduce((a, v) => a + v.value, 0n)}
          symbol="$"
          className="color-grey fs-14 condensed ml-8"
          decimals={8}
        />
      </td>
      <td>
        <div className="tag tag--borderless tag--grey">
          {(Number(p.debt.rate) / 1e10).toLocaleDecimal(2)}%
        </div>
      </td>
      <td>
        <LtvLabel ltv={BigInt(data.ltv)} />
      </td>
      <td>
        <div className="flex">
          <LiquidationPrice p={p} />
        </div>
      </td>
      <td>
        <Button
          className="button--xs button--outline button--icon-right button--grey"
          label="Manage">
          <Icons.ArrowUpRight />
        </Button>
      </td>
    </tr>
  );
};

const PostitionRowCollateral: FC<
  PositionBorrowRowFragment$data["collaterals"][0]
> = (val) => {
  switch (val.collateral.__typename) {
    case "Balance":
      return (
        <div className="flex ai-c">
          <IconDenom
            denom={val.collateral.asset.metadata.symbol}
            className="w-2.5 mr-1"
          />
          <Decimal
            amount={BigInt(val.collateral.amount)}
            symbol={val.collateral.asset.metadata.symbol}
            className="condensed fw-500"
          />
        </div>
      );

    default:
      return null;
  }
};

const PostitionRowDebt: FC<PositionBorrowRowFragment$data["debts"][0]> = (
  val
) => {
  return (
    <div className="flex ai-c">
      <IconDenom
        denom={val.debt.borrower.asset.metadata.symbol}
        className="w-2.5 mr-1"
      />
      <Decimal
        amount={BigInt(val.debt.current)}
        symbol={val.debt.borrower.asset.metadata.symbol}
        className="condensed fw-500"
      />
    </div>
  );
};
